import { useState, useEffect } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useActiveStore } from "@/hooks/use-active-store";
import { usePickPackSettings, useSavePickPackSettings } from "@/hooks/use-fulfillment";

export function PickPackSettingsDialog() {
  const { storeId } = useActiveStore();
  const { data: settings } = usePickPackSettings(storeId);
  const save = useSavePickPackSettings();

  const [open, setOpen] = useState(false);
  const [imageSize, setImageSize] = useState(40);
  const [locationFontSize, setLocationFontSize] = useState(12);
  const [hideWarehouseName, setHideWarehouseName] = useState(false);

  useEffect(() => {
    if (settings) {
      setImageSize(settings.imageSize);
      setLocationFontSize(settings.locationFontSize);
      setHideWarehouseName(settings.hideWarehouseName);
    }
  }, [settings]);

  function handleSave() {
    if (!storeId) return;
    save.mutate(
      { storeId, imageSize, locationFontSize, hideWarehouseName },
      {
        onSuccess: () => {
          toast.success("Postavke sačuvane");
          setOpen(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
          <Settings2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Postavke prikaza</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm">Veličina slike</label>
              <span className="text-sm tabular-nums font-medium">{imageSize}px</span>
            </div>
            <input
              type="range"
              min={0}
              max={80}
              step={4}
              value={imageSize}
              onChange={(e) => setImageSize(Number(e.target.value))}
              className="w-full accent-foreground"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Isključeno</span>
              <span>80px</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm">Veličina lokacije</label>
              <span className="text-sm tabular-nums font-medium">{locationFontSize}px</span>
            </div>
            <input
              type="range"
              min={10}
              max={24}
              step={1}
              value={locationFontSize}
              onChange={(e) => setLocationFontSize(Number(e.target.value))}
              className="w-full accent-foreground"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>10px</span>
              <span>24px</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <label className="text-sm">Sakrij naziv skladišta</label>
              <span className="text-[11px] text-muted-foreground">Prikaži samo lokaciju (npr. P1-B3)</span>
            </div>
            <Switch checked={hideWarehouseName} onCheckedChange={setHideWarehouseName} />
          </div>

          <div className="flex items-center gap-3 p-3 bg-muted/50 ring-1 ring-foreground/5">
            <div
              className="shrink-0 bg-muted ring-1 ring-foreground/10"
              style={{ width: imageSize || 0, height: imageSize || 0 }}
            />
            <span style={{ fontSize: locationFontSize }} className="font-medium tabular-nums">
              {hideWarehouseName ? "P1-B3" : "Magacin A → P1-B3"}
            </span>
          </div>

          <Button onClick={handleSave} disabled={save.isPending} className="w-full">
            {save.isPending ? "Čuvanje..." : "Sačuvaj"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
